/* eslint-disable react/destructuring-assignment */
import React, { useEffect, useState, useRef } from 'react';
import { useDispatch, connect, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { useIntl } from 'react-intl';
import * as showToastAction from '@modules/toasts/features/toastsSlice';
import { useGetCreabotsProject, useSaveCreabotsProgress } from '@services/rest/creabots/hooks';
import * as routes from '@educabot/educablocks-cosmos';
import { v1 as uuidv1 } from 'uuid';
import Header from '@sections/creabots/components/header';
import Bottom from '@sections/creabots/components/bottom';
import Tutorial from '@sections/creabots/containers/tutorial';
import Blockly from '@modules/blockly/containers/blockly';
import BlocklyBuild from '@modules/blockly/components/blocklyBuild';
import { build, setMLSamplesModalActive, webSerialModalClose } from '@modules/blockly/features/blocklySlice';
import { useCheckLogged } from '@utils/hooks/auth';
import { Kits } from '@sections/creabots/features/kits';
import { boards as Boards, getBoardById } from '@educabot/educablocks-boards';
import ConnectionModal from '@sections/creabots/components/connectionModal';
import BuildProgressModal from '@sections/creabots/components/buildProgressModal';
import { useBlockly } from '@modules/blockly/features/hooks';
import * as creabotsSlice from '@sections/creabots/features/creabotsSlice';
import { getProjectsProgress, INITIATION_KIT_TUTORIAL_ID_TYPE } from '@services/rest/creabots/getProjectsProgress';
import { getUserAgent } from '../../../helpers/browserAgent';
import Agent from '../../../helpers/agent';
import { store } from '../../../state/store';
import styles from './project.mod.scss';
import { CREABOTS_KIT_ID_TYPE, MODAL_TYPE } from '../types/kits';
import firstSteps from '../features/kits/initiation/projects/firstSteps';
import MLPanel from '@modules/blockly/components/MLPanel'
import { TRAIN_STEPS, MODEL_TYPES } from '@utils/hooks/machineLearning/params';
import {
  setMLModel,
  setMLModelJson,
  setMLModelWeights,
  setMLTrainingParams,
  setMLTrainingStep,
  setMLSamples,
  setMLModelTrained,
} from '@modules/blockly/features/blocklySlice';
import EmptyStateIcon from '@assets/icons/emptyStateIcon';
import EmptyState from '../components/emptyState';


const CreabotsProject = (props) => {
  const intl = useIntl();
  const location = useLocation();
  const dispatch = useDispatch();
  const { kits } = useSelector(creabotsSlice.selectCreabots)
  const [id, setId] = useState(0);
  const [board, setBoard] = useState({});
  const [workspace, setWorkspace] = useState(null);
  const [projectId, setProjectId] = useState(null);
  const [kitName, setKitName] = useState(null);
  const [kitTutorial, setKitTutorial] = useState(null);
  const [kitProject, setKitProject] = useState(null);
  const [tabInfo, setTabInfo] = useState({});
  const [socket, setSocket] = useState(null);
  const [userAgent, setUserAgent] = useState({});
  const [blocklyId, setBlocklyId] = useState(uuidv1());
  const [iaModel, setIAModel] = useState(null);
  const [intervalPortListing, setIntervalPortListing] = useState(null);
  const projectState = useGetCreabotsProject(Kits[kitName]?.id || 0, projectId || 0);
  const userDataState = useCheckLogged(true, '/kits/ingresar');
  const blocklyState = useBlockly();
  const blocklyRef = useRef();
  const { saveCreabotsProgress } = useSaveCreabotsProgress();
  const progressLoadingRef = useRef();
  const history = useHistory();
  const mlHandler = useRef();

  const isFirstStepsProject = (project) => {
    return (
      project.creabotsKitId === CREABOTS_KIT_ID_TYPE.INITIATION
      && project.tutorialId === INITIATION_KIT_TUTORIAL_ID_TYPE.FIRST_STEPS
    );
  }

  useEffect(() => {
    const checkFirstStepsProgress = async () => {
      const projectsProgress = await getProjectsProgress();
      const firstStepsProjectLoaded = projectsProgress.find(isFirstStepsProject);
      const currentProjectIsFirstSteps = kitName === 'iniciacion' && projectId === firstSteps.id;

      if (!firstStepsProjectLoaded && !currentProjectIsFirstSteps) {
        history.replace(`/kits/creabots/${kits.selected.urlName}`)
      }

      const couldFinishFirstStepProject = firstStepsProjectLoaded.isCompleted

      if (!couldFinishFirstStepProject && !currentProjectIsFirstSteps) {
        history.replace(`/kits/creabots/${kits.selected.urlName}`)
      }
    }

    if (kitName && projectId) {
      checkFirstStepsProgress();
    }
  }, [kitName, projectId]);

  useEffect(() => {
    progressLoadingRef.current = projectState?.loading || false;
  }, [projectState?.loading]);

  useEffect(() => {
    if (kitProject) {
      setTabInfo({
        tagsSelected: [],
        projectId: projectState.progress?.project?.id || 0,
        id: blocklyId,
        name: projectState.progress?.project?.title || intl.formatMessage({ id: kitProject.name }) || '',
        description: projectState.progress?.project?.description || intl.formatMessage({ id: kitProject.description }) || '',
        userId: projectState.progress?.project?.userId || 0,
        board,
        port: projectState.progress?.project?.jsonBody?.port || '',
        agentPorts: [],
        boardName: board.name || '',
        className: board.class || '',
        portOpen: false,
        active: true,
        disabled: false,
        withToolTip: false,
        withWorkspace: projectState.progress?.project?.jsonBody?.withWorkspace || false,
        rightPanel: '',
        diagramImageUrl: projectState.progress?.project?.imageUrl || '',

        boardType: 'senior',
        code: projectState.progress?.project?.jsonBody?.code || '',
      });

      const machineLearning = projectState.progress.project?.jsonBody?.machineLearning || {};
      if (machineLearning.model) {
        dispatch(setMLModel(machineLearning.model || null));
        dispatch(setMLModelJson(machineLearning.modelJson || null));
        dispatch(setMLModelWeights(machineLearning.modelWeights || null));
        dispatch(setMLTrainingParams(machineLearning.trainingParams || {}));
        dispatch(setMLTrainingStep(machineLearning.trainingStep || TRAIN_STEPS.TEACH));
        dispatch(setMLSamples(machineLearning.samples || {}));
        dispatch(setMLModelTrained(machineLearning.modelTrained || false));
        dispatch(setMLSamplesModalActive(machineLearning.samplesModalActive || null));
      }

      startSocket(true);
    }
  }, [board, kitProject, projectState?.progress?.project?.id]);

  useEffect(() => {
    if (projectState?.progress?.kitId && kitTutorial) {
      setKitTutorial(kitTutorial.map((tutorial, index) => {
        tutorial.completed = (index <= projectState.progress.step);
        return { ...tutorial };
      }));
    }
  }, [projectState?.progress?.kitId]);

  useEffect(() => {
    const pathSegment = location
      .pathname
      .split('/')
      .filter(Boolean)[1];

    if (pathSegment) {
      const parts = pathSegment.split('-').filter(Boolean);

      // Try to find the projectId (should be a number at the end)
      const projectIdStr = parts[parts.length - 1];
      const parsedProjectId = parseInt(projectIdStr, 10);

      if (!isNaN(parsedProjectId)) {
        setProjectId(parsedProjectId);
        // Kit name is everything before the last part (projectId)
        setKitName(parts.slice(0, -1).join('-') || null);
      } else {
        // Fallback to original logic if no valid number found
        setProjectId(0);
        setKitName(parts[0] || null);
      }
    } else {
      setProjectId(0);
      setKitName(null);
    }

    return () => {
      setId(0);
      setWorkspace(false);
      setBlocklyId(uuidv1());
      setSocket(null);
      setKitName(null);
      setProjectId(null);
      setKitProject(null);
      setBoard({});
    };
  }, []);

  useEffect(() => {
    if (kitName && projectId && Kits[kitName]) {
      Kits[kitName].projects.map((p) => {
        if (p.id === projectId) {
          if (p.tutorial?.length > 0) {
            setKitTutorial(p.tutorial);
          }
          setKitProject(p);
          setIAModel(p.iaModelType || null);
          setBoard(getBoardById(Boards, p.boardId));
        }
      })

      if (kits.selected.urlName !== kitName) {
        dispatch(creabotsSlice.setSelectedKit({
          id: projectId,
          urlName: kitName,
        }));
      }
    }
  }, [kitName, projectId]);

  useEffect(() => {
    if (socket && !intervalPortListing) {
      const intervalPortListing = setInterval(() => {
        socket.findPorts();
      }, 1000);
      setIntervalPortListing(intervalPortListing);
    }

    return () => {
      clearInterval(intervalPortListing);
      setIntervalPortListing(null);
    }
  }, [socket?.agentType]);

  useEffect(() => {
    blocklyRef.current = blocklyState?.project || {};
  }, [blocklyState?.project?.withWorkspace, blocklyState?.project?.code]);

  const startSocket = () => {
    const newSocket = (board.profile !== 'codit' && board.profile !== 'drone')
      ? new Agent(store, board)
      : null

    if (newSocket?.agentType) {
      getUserAgent().then((ua) => {
        setUserAgent(ua);
        newSocket.setPlatform(ua.platform, 4000);
        // Hack to force redraw in order to update socket.agentType property
        setTimeout(() => {
          setId(1);
        }, 4000);
      });
    }

    setSocket(newSocket);
  }

  const setAgent = (type = 'serial') => {
    clearInterval(intervalPortListing);
    setIntervalPortListing(null);
    socket.forceAgent(type);
  }

  const buildBlockly = (params) => {
    dispatch(build(params));
    if (blocklyState.webSerialModal.step === 5) {
      dispatch(webSerialModalClose());
    }
  }

  const changeBuildPort = (name) => {
    // setTabInfo({ ...tabInfo, port: name });
  }

  const updateWorkspace = (ws) => {
    if (ws) {
      setWorkspace(ws);
    }
  }

  const handleGoHome = () => {
    window.location.href = `${routes.bloquesUri}/kits/creabots/${kits.selected.urlName}`;
  }

  const handleSetMLHandler = (currentMLHandler) => {
    mlHandler.current = currentMLHandler;
  }

  const handleSaveProgress = (step = 0) => {
    if (userDataState.data?.userId && !progressLoadingRef.current) {
      const projectDetails = {
        userId: userDataState.data.userId,
        title: tabInfo.name,
        description: tabInfo.description,
        boardType: tabInfo.boardType,
        jsonBody: {
          machineLearning: {
            active: blocklyState?.machineLearning?.active || false,
            model: blocklyState?.machineLearning?.model || null,
            trainingStep: blocklyState?.machineLearning?.trainingStep || TRAIN_STEPS.TEACH,
            trainingParams: blocklyState?.machineLearning?.trainingParams || {},
            samples: (iaModel === MODEL_TYPES.SOUNDS && mlHandler.current) ? mlHandler.current.getSamples(true) : blocklyState?.machineLearning?.samples || {},
            modelTrained: blocklyState?.machineLearning?.modelTrained || false,
            modelJson: blocklyState?.machineLearning?.modelJson || null,
            modelWeights: blocklyState?.machineLearning?.modelWeights || null,
            samplesModalActive: blocklyState?.machineLearning?.samplesModalActive || false,
          },
          ...tabInfo,
          withWorkspace: blocklyRef.current.withWorkspace,
          code: blocklyRef.current.code,
          canvasCode: blocklyRef.current.code,
          canvas: blocklyRef.current.canvas,
          canvasBBoxX: blocklyRef.current.canvasBBoxX,
          canvasBBoxY: blocklyRef.current.canvasBBoxY,
          canvasBBoxWidth: blocklyRef.current.canvasBBoxWidth,
          canvasBBoxHeight: blocklyRef.current.canvasBBoxHeight,
        },
        canvasCss: blocklyRef.current.canvasCss,
        blocksCss: blocklyRef.current.blocksCss,
        isCreabots: true,
        isPublic: false,
        isLibrary: false,
        imageUrl: '',
      };
      const progressDetails = {
        creabotsKitsId: Kits[kitName].id,
        tutorialId: projectId,
        userId: userDataState.data.userId,
        step,
        isCompleted: step === kitTutorial.length - 2,
      };

      saveCreabotsProgress(progressDetails, projectDetails);
    }
  }

  const [showEmptyState, setShowEmptyState] = useState(false);

  useEffect(() => {
    let timeout;
    if (!(tabInfo.id && board.id)) {
      timeout = setTimeout(() => setShowEmptyState(true), 350);
    } else {
      setShowEmptyState(false);
    }
    return () => clearTimeout(timeout);
  }, [tabInfo.id, board.id]);

  return (
    <div className={styles.project}>
      {tabInfo.id && board.id ? (
        <Header
          title={kitProject?.name || ''}
          handleGoHome={handleGoHome}
        />
      ) : null}
      <div className={`${styles.courses} ${iaModel ? styles.iaPanel : ''}`}>
        {tabInfo.id && board.id ? (
          <>
            <Blockly
              tabInfo={tabInfo}
              socketAgent={socket}
              buildAllowed={false}
              portChangeAllowed={true}
              setWorkspace={updateWorkspace}
              noCodePanel={true}
              isCreabots={true}
              isFreeProject={false}
            />
            {socket && tabInfo.id && board.id ? (
              <Bottom
                buildingFunction={buildBlockly}
                board={tabInfo.board}
                socketAgent={socket}
              />
            ) : null}
            {tabInfo.id && board.id ? (
              <BlocklyBuild
                board={tabInfo.board}
                socketAgent={socket}
                id={tabInfo?.id || ''}
                compilerBoard={tabInfo?.board?.compilerBoard || ''}
                userAgent={userAgent}
                userData={userDataState?.data || {}}
                setAgent={setAgent}
                changeBuildPort={changeBuildPort}
                mainModal={ConnectionModal}
                progressModal={BuildProgressModal}
                buildingFunction={buildBlockly}
              />
            ) : null}
            {iaModel && tabInfo.id && board.id ? (
              <MLPanel
                isCreabots={true}
                isFreeProject={false}
                workspace={workspace}
                iaModel={iaModel || null}
                board={tabInfo.board || {}}
                setMLHandler={handleSetMLHandler}
              />
            ) : ('')}
            <Tutorial
              id={tabInfo.id || ''}
              data={kitTutorial}
              workspace={workspace}
              onSaveProgress={handleSaveProgress}
            />
          </>
        ) : (
          showEmptyState && (
            <div>
              <EmptyState />
            </div>
          )
        )}
      </div>
    </div>
  );
};


const mapDispatchToProps = (dispatch) => {
  return {
    showToast: (params) => dispatch(showToastAction.showToast(params)),
  }
}

export default connect(null, mapDispatchToProps)(CreabotsProject);
